import {
  PAYROLL_VOUCHER_STATUS_LABELS,
  PayrollVoucherStatusEnum,
} from "@/enum/payroll-voucher-status";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { formatToBRL } from "@/utils/formatToMoney";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";
import { Link2 } from "lucide-react";
import { DataTableColumnHeader } from "./components/data-table-column-header";
import { VoucherRowActions } from "./components/voucher-row-actions";
import {
  COLUMN_TITLES,
  FIRST_INSTALLMENT_INDEX,
  MIN_INSTALLMENTS,
  STATUS_BADGE_CLASS_NAME,
} from "./constants";
import { redactCpf } from "@/utils/cpf-mask";
import { PAYROLL_VOUCHER_LABELS } from "./messages";
import { PayrollVoucher, VoucherDecision } from "./types";
import {
  installmentAmountsOf,
  installmentsOf,
} from "./utils/decision-consequence";

export const INSTALLMENTS_TEST_ID: string = "voucher-installments-column";
export const DIFFERENCE_DATE_TEST_ID: string = "voucher-difference-date";

export interface VoucherTableMeta {
  canDecide: boolean;
  onDecide: (
    voucher: PayrollVoucher,
    decision: VoucherDecision,
  ) => Promise<void>;
}

const isSelectable = (voucher: PayrollVoucher): boolean =>
  voucher.status === PayrollVoucherStatusEnum.PENDING;

export const voucherColumns: ColumnDef<PayrollVoucher>[] = [
  {
    id: "select",
    header: ({ table }) => {
      const selectable = table
        .getRowModel()
        .rows.filter((row) => isSelectable(row.original));

      if (selectable.length === 0) {
        return (
          <Checkbox
            checked={false}
            disabled
            aria-label={PAYROLL_VOUCHER_LABELS.SELECT_NONE_AVAILABLE}
          />
        );
      }

      const allSelected = selectable.every((row) => row.getIsSelected());
      const someSelected = selectable.some((row) => row.getIsSelected());

      return (
        <Checkbox
          checked={allSelected || (someSelected && "indeterminate")}
          onCheckedChange={(value) =>
            selectable.forEach((row) => row.toggleSelected(!!value))
          }
          aria-label={PAYROLL_VOUCHER_LABELS.SELECT_ALL}
        />
      );
    },
    cell: ({ row }) =>
      isSelectable(row.original) ? (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={PAYROLL_VOUCHER_LABELS.SELECT_ROW}
        />
      ) : null,
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: "conciliation_closed_at",
    accessorKey: "conciliation_closed_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={COLUMN_TITLES.DATE} />
    ),
    cell: ({ row }) => {
      const voucher: PayrollVoucher = row.original;

      // Vale anterior ao backfill nao tem data de fechamento: cai na data da
      // diferenca para a celula nao nascer vazia.
      if (!voucher.conciliation_closed_at)
        return (
          <span className="whitespace-nowrap text-gray-700">
            {formatDateToDMY(voucher.conciliation_date)}
          </span>
        );

      return (
        <div className="flex flex-col gap-0.5">
          <span className="whitespace-nowrap text-gray-700">
            {formatDateToDMY(voucher.conciliation_closed_at)}
          </span>
          <span
            className="whitespace-nowrap text-xs text-gray-500"
            data-testid={`${DIFFERENCE_DATE_TEST_ID}-${voucher.id}`}
          >
            {PAYROLL_VOUCHER_LABELS.DIFFERENCE_AT(
              formatDateToDMY(voucher.conciliation_date),
            )}
          </span>
        </div>
      );
    },
  },
  {
    id: "employee_name",
    accessorKey: "employee_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={COLUMN_TITLES.NAME} />
    ),
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-gray-900">
          {row.original.employee_name}
        </span>
        <span
          className="flex items-center gap-1 text-xs text-gray-500"
          title={PAYROLL_VOUCHER_LABELS.CONCILIATION_LINK_TITLE}
        >
          <Link2 className="h-3 w-3" />
          {PAYROLL_VOUCHER_LABELS.CONCILIATION_LINK(
            row.original.conciliation_id,
          )}
        </span>
        {row.original.analyst_justification && (
          <span className="w-fit rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            {row.original.analyst_justification}
          </span>
        )}
      </div>
    ),
  },
  {
    id: "employee_cpf",
    accessorKey: "employee_cpf",
    header: COLUMN_TITLES.CPF,
    cell: ({ row }) => (
      <span className="font-mono text-xs text-gray-700">
        {redactCpf(row.original.employee_cpf)}
      </span>
    ),
    enableSorting: false,
  },
  {
    id: "store_code",
    accessorKey: "store_code",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={COLUMN_TITLES.BRANCH} />
    ),
    cell: ({ row }) => (
      <span className="text-gray-700">{row.original.store_code}</span>
    ),
  },
  {
    id: "amount",
    accessorKey: "amount",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={COLUMN_TITLES.AMOUNT}
        className="ml-auto"
      />
    ),
    cell: ({ row }) => (
      <div className="text-right font-medium text-gray-900">
        {formatToBRL(row.original.amount)}
      </div>
    ),
  },
  {
    id: "installments_count",
    header: COLUMN_TITLES.INSTALLMENTS,
    cell: ({ row }) => {
      const installments: number = installmentsOf(row.original);
      const first: number =
        installmentAmountsOf(row.original)[FIRST_INSTALLMENT_INDEX] ?? 0;

      return (
        <div
          className="flex flex-col gap-0.5"
          data-testid={`${INSTALLMENTS_TEST_ID}-${row.original.id}`}
        >
          <span className="w-fit rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-800">
            {`${installments}x`}
          </span>
          {installments > MIN_INSTALLMENTS && (
            <span className="text-xs text-gray-500">
              {formatToBRL(Math.abs(first))}
            </span>
          )}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: "status",
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={COLUMN_TITLES.STATUS} />
    ),
    cell: ({ row }) => {
      const voucher: PayrollVoucher = row.original;

      return (
        <div className="flex flex-col gap-0.5">
          <span
            className={`w-fit rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASS_NAME[voucher.status]}`}
            title={voucher.rejection_reason ?? undefined}
          >
            {PAYROLL_VOUCHER_STATUS_LABELS[voucher.status]}
          </span>
          {voucher.decided_by_email && (
            <span className="text-xs text-gray-500">
              {PAYROLL_VOUCHER_LABELS.DECIDED_BY(voucher.decided_by_email)}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: "decision",
    header: COLUMN_TITLES.DECISION,
    cell: ({ row, table }) => {
      const { canDecide, onDecide } = table.options.meta as VoucherTableMeta;

      return (
        <VoucherRowActions
          voucher={row.original}
          canDecide={canDecide}
          onDecide={onDecide}
        />
      );
    },
    enableSorting: false,
  },
];
